/**
 * SetupChecklistInline — collapsible "Get Started with CountBucks" panel.
 *
 * Lifted from CEOCommandCenter so it can be reused at the top of the unified
 * AI Studio hero state. Renders 3 onboarding steps (bank, contacts, documents)
 * with live progress from `/api/workspaces/{ws}/onboarding-progress`.
 *
 * Hides itself entirely when all 3 steps are complete AND the user previously
 * collapsed it — keeps the hero clean for advanced users.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import {
  Sparkles, ChevronDown, ChevronRight, ArrowRight, Check, Circle,
  Building2, Users, FolderPlus,
} from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SETUP_STEPS = [
  { key: 'bank',      label: 'Connect your first bank account', icon: Building2, to: '/banking' },
  { key: 'contacts',  label: 'Add a customer or vendor',         icon: Users,     to: '/contacts' },
  { key: 'documents', label: 'Set up your document hub',         icon: FolderPlus, to: '/documents' },
];

export default function SetupChecklistInline() {
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem('cc_setup_collapsed_v1') !== '1'; } catch { return true; }
  });

  const fetchProgress = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/onboarding-progress`);
      if (r.ok) setProgress(await r.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [wsId]);

  useEffect(() => { fetchProgress(); }, [fetchProgress]);

  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      try { localStorage.setItem('cc_setup_collapsed_v1', next ? '0' : '1'); } catch { /* */ }
      return next;
    });
  };

  if (loading || !progress) return null;

  const steps = progress.steps || {};
  const doneCount = SETUP_STEPS.filter((s) => steps[s.key]?.done).length;
  const pct = Math.round((doneCount / SETUP_STEPS.length) * 100);

  // Auto-hide when everything is done AND user previously collapsed — they
  // don't need it anymore.
  if (doneCount === SETUP_STEPS.length && !open) return null;

  return (
    <div className="rounded-xl border bg-card overflow-hidden" data-testid="setup-checklist-inline">
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center gap-3 p-3 hover:bg-muted/40 transition-colors text-left"
        data-testid="setup-toggle"
      >
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-amber-500 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-emerald-700 dark:text-emerald-400">
            Get Started with CountBucks
          </p>
          <p className="text-xs text-muted-foreground truncate">
            {doneCount} of {SETUP_STEPS.length} complete · {pct}%
            {doneCount === SETUP_STEPS.length ? ' — All set!' : ' — Tap to expand'}
          </p>
        </div>
        <div className="hidden sm:block w-24 h-1.5 rounded-full bg-muted overflow-hidden">
          <div className="h-full bg-gradient-to-r from-emerald-500 to-amber-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
      </button>
      {open && (
        <div className="border-t px-3 py-3 space-y-1.5" data-testid="setup-content">
          {SETUP_STEPS.map((s) => {
            const done = !!steps[s.key]?.done;
            const StepIcon = s.icon;
            return (
              <Link
                key={s.key}
                to={s.to}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-lg border transition-colors',
                  done
                    ? 'border-emerald-200 dark:border-emerald-900/40 bg-emerald-50/40 dark:bg-emerald-950/15'
                    : 'border-border hover:bg-muted/30',
                )}
                data-testid={`setup-step-${s.key}`}
              >
                <div className={cn(
                  'w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0',
                  done ? 'bg-emerald-500 text-white' : 'bg-muted text-muted-foreground',
                )}>
                  {done ? <Check className="w-4 h-4" /> : <StepIcon className="w-3.5 h-3.5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={cn('text-sm font-medium', done && 'text-emerald-700 dark:text-emerald-300 line-through opacity-75')}>
                    {s.label}
                  </p>
                </div>
                {!done && <ArrowRight className="w-4 h-4 text-muted-foreground" />}
                {done && <Circle className="w-3 h-3 text-emerald-500 fill-emerald-500" />}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
